import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'dark' | 'cream' }) {
  const styles = {
    // Green: the one thing to do on this screen.
    primary: 'bg-accent text-on-accent shadow-[inset_0_1px_0_rgb(255_255_255/0.35),0_1px_2px_rgb(2_53_42/0.14)] hover:bg-accent-strong disabled:opacity-50',
    secondary: 'bg-surface text-ink border border-line shadow-[0_1px_1px_rgb(2_53_42/0.05)] hover:bg-subtle disabled:opacity-50',
    ghost: 'text-ink hover:bg-subtle disabled:opacity-50',
    // Dark green: strong, but not the main action (on paper), or a raised button on the dark desk.
    dark: 'bg-frame text-canvas hover:bg-frame-soft disabled:opacity-50',
    // The one thing to do on a dark green card.
    cream: 'bg-cream text-frame font-semibold hover:bg-white disabled:opacity-50',
  }[variant];
  return (
    <button
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-5 text-[15px] font-medium transition-colors ${styles} ${className}`}
      {...props}
    />
  );
}

export function Field({
  label,
  hint,
  id,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; id: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        className="min-h-11 rounded-[var(--radius-control)] border border-line bg-surface px-3 text-base shadow-[0_1px_1px_rgb(2_53_42/0.04)] outline-none"
        {...props}
      />
      {hint && <p className="text-sm text-muted">{hint}</p>}
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-[var(--radius-card)] border border-line bg-surface p-5 shadow-[0_1px_2px_rgb(2_53_42/0.04)] ${className}`}>{children}</div>;
}

export function ErrorNote({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-sm text-danger">
      {message}
    </p>
  );
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <Card className="flex flex-col items-start gap-2 py-10">
      <h2 className="display text-[30px]">{title}</h2>
      <p className="max-w-prose text-muted">{body}</p>
      {action}
    </Card>
  );
}
